import { inject, Injectable } from '@angular/core';
import {
    AuthorizationsObject,
    AuthService,
    OAuthProfile,
    UserProfileService,
} from '@dat/angular-auth';
import {
    IqEventBusBrokerService
} from '@dat/iq-event-bus';
import { isMciUpdating } from '@dat/iq-mc-utils';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { isEmpty } from 'lodash';
import { combineLatest, filter, switchMap } from 'rxjs';

type UserState = {
    fetch: boolean;
    userProfile?: OAuthProfile;
    authorizations?: AuthorizationsObject;
};

@Injectable({
    providedIn: 'root',
})
export class IqMcRulesRegisterService extends ComponentStore<UserState> {
    private readonly _auth: AuthService = inject(AuthService);
    private readonly _user: UserProfileService = inject(UserProfileService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _registered: boolean = false;
    constructor() {
        super({
            fetch: false,
        });

        // effect(() => {
        //     console.log(`MC Rules State`);
        //     console.log(this.state());
        // });
    }

    register = this.effect<void>((trigger$) => {
        if (this._registered) return null;
        return trigger$.pipe(
            tapResponse({
                next: () => {
                    this._bus.dispatch({
                        name: 'type:rules:evaluator:register',
                        payload: {
                            evaluator: (): Promise<boolean> => {
                                return this._isMciUpdating();
                            },
                            type: 'mci:updating',
                        },
                    });
                    this._bus.dispatch({
                        name: 'type:rules:evaluator:register',
                        payload: {
                            evaluator: ({
                                permissions,
                            }: {
                                permissions: string[];
                            }): Promise<boolean> => {
                                return this._evaluateUserCapabilities(
                                    permissions,
                                );
                            },
                            type: 'mc:user:capabilty:evaluator',
                        },
                    });
                    this._registered = true;
                    this.patchUserCapabilities();
                },
                error: (error) => {
                    console.error('Error registering MC Rules', error);
                },
            }),
        );
    });

    patchUserCapabilities = this.effect<void>(($trigger) => {
        return $trigger.pipe(
            switchMap(() => {
                return combineLatest([
                    this._user.userProfile$,
                    this._auth.userAuthorizationsResult$,
                ]);
            }),
            filter(
                ([user, authorizations]: [
                    OAuthProfile,
                    AuthorizationsObject,
                ]) => !isNaN(user.csbId) && !!authorizations,
            ),
            tapResponse({
                next: async ([user, authorizations]: [
                    OAuthProfile,
                    AuthorizationsObject,
                ]) => {
                    this.patchState({
                        ...this.state(),
                        fetch: true,
                        userProfile: user,
                        authorizations: authorizations,
                    });
                },
                error: (error) => {
                    console.error(
                        'Error registering user capabilities rule',
                        error,
                    );
                },
            }),
        );
    });

    isCapabiltyFetched = this.selectSignal((state): boolean => {
        return this._registered && state.fetch;
    });

    getAuthorizations = this.selectSignal((state) => state.authorizations);

    private _evaluateUserCapabilities(permissions: string[]): Promise<boolean> {
        const resolve = new Promise<boolean>((resolve) => {
            if (this.isCapabiltyFetched()) {
                this._hasCapability(permissions, resolve);
                return;
            }

            const intervalId = setInterval(() => {
                if (!this.isCapabiltyFetched()) {
                    return;
                }
                clearInterval(intervalId);
                this._hasCapability(permissions, resolve);
            }, 50);
        });
        return resolve;
    }

    private _hasCapability(
        permissions: string[],
        resolve: (value: boolean) => void,
    ) {
        const authorizations = this.getAuthorizations();
        if (!isEmpty(permissions)) {
            const hasPermission = permissions.every((permission) => {
                return authorizations?.permissions.includes(permission);
            });
            return resolve(hasPermission);
        }
        return resolve(false);
    }

    private _isMciUpdating(): Promise<boolean> {
        const isUpdating: boolean = isMciUpdating();
        return Promise.resolve(isUpdating);
    }
}
